import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Statistic, Progress, List, Avatar, Tag, Spin, Alert, Button, Space } from 'antd';
import { ClockCircleOutlined, ProjectOutlined, CalendarOutlined, CheckCircleOutlined, UserOutlined, PlusOutlined, HistoryOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const EmployeeDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Debug user data structure
      console.log('User data:', user);
      
      // Check if user and user ID exist
      if (!user) {
        setError('User not authenticated');
        return;
      }
      
      // Extract user data from the response structure
      const userData = user.data || user;
      const userId = userData.id || userData._id || userData.employeeId;
      
      if (!userId) {
        setError('User ID missing from authentication data');
        console.error('Available user fields:', Object.keys(userData));
        return;
      }
      
      console.log('Using user ID:', userId);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/employee/${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  const { employee, allocatedProjects, projectStats, recentTimesheets, summary } = dashboardData || {};

  return (
    <div className="p-6">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border mb-6">
        <Title level={2} className="mb-2">Welcome back, {employee?.name}!</Title>
        <Text className="text-gray-600">
          {employee?.position} • {employee?.department}
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* Main Content - 70% */}
        <Col xs={24} lg={17}>
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card title="Quick Actions" className="shadow-sm">
              <Space size="large">
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  size="large"
                  onClick={() => navigate('/timesheet')}
                >
                  Submit Timesheet
                </Button>
                <Button 
                  icon={<HistoryOutlined />} 
                  size="large"
                  onClick={() => navigate('/history')}
                >
                  View History
                </Button>
              </Space>
            </Card>

            {/* Current Projects */}
            <Card title="Your Active Projects" className="shadow-sm">
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {allocatedProjects?.map((project, index) => (
                  <div key={project._id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <Title level={5} className="mb-1">{project.name}</Title>
                        <Text className="text-gray-600">{project.client}</Text>
                        <div className="mt-2">
                          <Tag color={project.status === 'active' ? 'green' : 'blue'}>
                            {project.status.toUpperCase()}
                          </Tag>
                        </div>
                      </div>
                      <div className="text-right">
                        <Text className="text-sm text-gray-500">
                          {project.startDate ? dayjs(project.startDate).format('MMM DD, YYYY') : 'No start date'}
                        </Text>
                      </div>
                    </div>
                    {project.description && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <Text className="text-sm text-gray-600">{project.description}</Text>
                      </div>
                    )}
                  </div>
                ))}
                {(!allocatedProjects || allocatedProjects.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <ProjectOutlined className="text-4xl mb-2" />
                    <div>No projects assigned yet</div>
                  </div>
                )}
              </div>
            </Card>

            {/* Recent Activity Preview */}
            <Card title="Recent Activity" className="shadow-sm">
              <List
                dataSource={Array.isArray(recentTimesheets) ? recentTimesheets.slice(0, 5) : []}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<ClockCircleOutlined />} />}
                      title={`${item.hours}h on ${item.projectName}`}
                      description={`${dayjs(item.date).format('MMM DD, YYYY')} • ${item.description}`}
                    />
                    <Tag color={item.status === 'approved' ? 'green' : item.status === 'submitted' ? 'blue' : 'orange'}>
                      {item.status.toUpperCase()}
                    </Tag>
                  </List.Item>
                )}
              />
              {(!recentTimesheets || recentTimesheets.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <ClockCircleOutlined className="text-4xl mb-2" />
                  <div>No recent timesheet entries</div>
                </div>
              )}
            </Card>
          </div>
        </Col>

        {/* Sidebar - 30% */}
        <Col xs={24} lg={7}>
          <div className="space-y-6">
            {/* Summary Statistics */}
            <Card title="Summary" className="shadow-sm">
              <div className="space-y-4">
                <Statistic
                  title="Hours Today"
                  value={summary?.totalHoursToday || 0}
                  suffix="h"
                  prefix={<ClockCircleOutlined className="text-blue-500" />}
                />
                <Statistic
                  title="Hours This Month"
                  value={summary?.totalHoursThisMonth.toFixed(2) || 0}
                  suffix="h"
                  prefix={<CalendarOutlined className="text-green-500" />}
                />
                <Statistic
                  title="Active Projects"
                  value={summary?.totalProjects || 0}
                  prefix={<ProjectOutlined className="text-purple-500" />}
                />
                <Statistic
                  title="Total Entries"
                  value={summary?.totalEntries || 0}
                  prefix={<CheckCircleOutlined className="text-orange-500" />}
                />
              </div>
            </Card>

            {/* Project Insights */}
            <Card title="Project Insights" className="shadow-sm">
              <div className="space-y-4">
                {projectStats?.slice(0, 3).map((stat, index) => (
                  <div key={stat.projectName}>
                    <div className="flex justify-between items-center mb-2">
                      <Text strong className="text-sm">{stat.projectName}</Text>
                      <Text className="text-sm">{stat.totalHours}h</Text>
                    </div>
                    <Progress 
                      percent={Math.min((stat.totalHours / (projectStats[0]?.totalHours || 1)) * 100, 100)} 
                      size="small"
                      showInfo={false}
                    />
                    <Text className="text-xs text-gray-500">{stat.entriesCount} entries</Text>
                  </div>
                ))}
                {(!projectStats || projectStats.length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <div className="text-sm">No project data available</div>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Stats */}
            <Card title="This Week" className="shadow-sm">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Text>Average Daily Hours</Text>
                  <Text strong>{((summary?.totalHoursThisMonth || 0) / (summary?.uniqueDateCount)).toFixed(1)}h</Text>
                </div>
                <div className="flex justify-between">
                  <Text>Most Active Project</Text>
                  <Text strong className="text-sm">
                    {projectStats?.[0]?.projectName || 'None'}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text>Last Entry</Text>
                  <Text strong className="text-sm">
                    {recentTimesheets?.[0] ? dayjs(recentTimesheets[0].date).fromNow() : 'None'}
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EmployeeDashboard;
